import React, {useState} from "react";
import {useFormik} from "formik";
import {getTimezoneOptions, toSelectOptions} from "../../../../helpers/utilHelper";
import UsStates from "../../../../constants/usState";
import Select from "react-select";
import {Container, Button, Form, FormFeedback, Input, Label, Row, Col, Alert} from "reactstrap";
import * as Yup from "yup";
import regx from "../../../../constants/regx";
import classnames from "classnames";
import FormMessage from "../../../Shared/FormMessage";
import {oneStepAwayMessages} from "../../../../constants/form-messages";
import ReactGA from "react-ga4";
import ReCAPTCHA from "react-google-recaptcha";
import { ISelect, NotaryFormType } from "../../../../Interfaces/base";

const FormSection = () => {
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [isEmailErrorVisible, setIsEmailErrorVisible] = useState(false);
  const [textError, setTextError] = useState('');
  const [recaptchaValue, setRecaptchaValue] = useState(false);

  /********** FORM CONFIG **********/
  const formInitialValues = {
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    phone: '',
    timezone: '',
    state: '',
    termsAndConditions: false,
    recaptcha: false,
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: formInitialValues,
    validationSchema: Yup.object({
      firstName: Yup.string().trim().required('Mandatory field'),
      lastName: Yup.string().trim().required('Mandatory field'),
      title: Yup.string().trim().required('Mandatory field'),
      email: Yup.string().trim().required('Mandatory field').email('Please enter a valid email address'),
      phone: Yup.string().trim().required('Mandatory field').matches(regx.phone, 'Invalid phone number'),
      timezone: Yup.string().required('Mandatory field'),
      state: Yup.string().required('Mandatory field'),
      termsAndConditions: Yup.boolean().oneOf([true], 'Mandatory field'),
      recaptcha: Yup.boolean().oneOf([true], 'Please complete the reCAPTCHA'),
    }),
    onSubmit: values => handleFormSubmit(values)
  });

  /********** OTHER **********/
  const handleRecaptchaChange = (value:string) => {
    if (value) {
      setRecaptchaValue(true);
      formik.setFieldValue('recaptcha', true);
    }
  };

  const handleFormSubmit = async (values: NotaryFormType) => {
    const {termsAndConditions, ...payload} = values;
    try {
      const apiUrl = `${process.env.GATSBY_API_URL}/in/marketing/register-notary?authType=4&authToken=${process.env.GATSBY_MAV2_API_TOKEN}`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setIsSuccessOpen(true);
        formik.resetForm();
        formik.setSubmitting(false);

      } else if(response.status === 400) {
        const errorData = await response.json();
        setTextError(errorData.fields.email);
        setIsEmailErrorVisible(true);
      }
    } catch (error) {
      const errorData = await error.json();
      setTextError(errorData.fields.email);
      setIsEmailErrorVisible(true);
    }
  };

  const handleRegisterForm = () => {
    ReactGA.event({
      category: 'Button',
      action: 'notary_register_form_event',
      label: 'Notary Register Button'
    });
  }

  return(<section className="form-section">
    <Container className="container-fluid">
      <div className="sign-up-form">
        <div className="form-title">Let's sign you up</div>
        <Form className="mt-4" id="notary-form" onSubmit={formik.handleSubmit}>
          <Row>
            <Col xl="12" className="mb-3">
              <Label>First Name: *</Label>
              <Input
                type="text"
                placeholder="First Name"
                className="form-control"
                name="firstName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.firstName}
                invalid={formik.touched.firstName && !!formik.errors.firstName}
              />
              {formik.touched.firstName && formik.errors.firstName && <FormFeedback type="invalid">{formik.errors.firstName}</FormFeedback>}
            </Col>
          </Row>
          <Row>
            <Col xl="12" className="mb-3">
              <Label>Last Name: *</Label>
              <Input 
                type="text"
                placeholder="Last Name"
                className="form-control"
                name="lastName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                invalid={formik.touched.lastName && !!formik.errors.lastName}
              />
              {formik.touched.lastName && !!formik.errors.lastName && <FormFeedback type="invalid">{formik.errors.lastName}</FormFeedback>}
            </Col>
          </Row>
          <Row>
            <Col xl="12" className="mb-3">
              <Label>Title: *</Label>
              <Input 
                type="text"
                placeholder="Title"
                className="form-control"
                name="title"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
                invalid={formik.touched.title && !!formik.errors.title}
              />
              {formik.touched.title && !!formik.errors.title && <FormFeedback type="invalid">{formik.errors.title}</FormFeedback>}
            </Col>
          </Row>
          <Row>
            <Col xl="12" className="mb-3">
              <Label>Email: *</Label>
              <Input 
                type="email"
                placeholder="Email"
                className="form-control"
                name="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                invalid={formik.touched.email && !!formik.errors.email}
              />
              {formik.touched.email && !!formik.errors.email && <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>}
            </Col>
          </Row>
          <Row>
            <Col xl="12" className="mb-3">
              <Label>Phone Number: *</Label>
              <Input 
                type="text"
                placeholder="Phone Number"
                className="form-control"
                name="phone"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phone}
                invalid={formik.touched.phone && !!formik.errors.phone}
              />
              {formik.touched.phone && !!formik.errors.phone && <FormFeedback type="invalid">{formik.errors.phone}</FormFeedback>}
            </Col>
          </Row>
          <Row>
            <Col xl="12" className="mb-3">
              <Label>Timezone: *</Label>
              <Select
                placeholder="Choose"
                classNamePrefix="select2-selection"
                name="timezone"
                options={getTimezoneOptions()}
                onChange={(selected: ISelect | null) => {
                  if(selected) {
                    formik.setFieldValue('timezone', selected.value);
                  }
                }}
                onBlur={() => {
                  formik.setFieldTouched('timezone', true); // Manually update touched state
                  const timeZoneError = formik.errors.timezone;
                  if (timeZoneError) {
                    formik.setFieldError('timezone', timeZoneError);
                  }
                }}
                value={getTimezoneOptions().find(option => option.value === formik.values.timezone) || ''}
                className={formik.touched.timezone && !!formik.errors.timezone && 'is-invalid'} />
              {formik.touched.timezone && !!formik.errors.timezone && <FormFeedback type="invalid">{formik.errors.timezone}</FormFeedback>}
            </Col>
          </Row>
          <Row>
            <Col xl="12" className="mb-3">
              <Label>State: *</Label>
              <Select
                placeholder="Choose"
                classNamePrefix="select2-selection"
                name="state"
                options={usStates}
                onChange={(selected: ISelect | null) => {
                  if(selected) {
                    formik.setFieldValue('state', selected.value);
                  }
                }}
                onBlur={() => {
                  formik.setFieldTouched('state', true); // Manually update touched state
                  const stateError = formik.errors.state;
                  if (stateError) {
                    formik.setFieldError('state', stateError);
                  }
                }}
                value={usStates.find(option => option.value === formik.values.state) || ''}
                className={formik.touched.state && !!formik.errors.state && 'is-invalid'} />
              {formik.touched.state && !!formik.errors.state && <FormFeedback type="invalid">{formik.errors.state}</FormFeedback>}
            </Col>
          </Row>
          <Row className="mb-3 justify-content-center">
            <Col md="6" className="text-center">
              <div className="recaptcha-container">
                <ReCAPTCHA
                  sitekey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                  type="image"
                />
              </div>
              {formik.touched.recaptcha && formik.errors.recaptcha && (
                <div className="text-danger mt-2">{formik.errors.recaptcha}</div>
              )}
            </Col>
          </Row>
          <div className={classnames({ 'is-invalid': !!formik.errors.termsAndConditions }, 'terms-and-conditions')}>
            <Input 
              type="checkbox"
              className="form-check-input"
              id="termsAndConditions"
              name="termsAndConditions"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              checked={formik.values.termsAndConditions}
              invalid={formik.touched.termsAndConditions && !!formik.errors.termsAndConditions}
            />
            <Label htmlFor="termsAndConditions" className='ms-2'>Accept <a href={'/terms-and-conditions'}>Terms and Conditions</a></Label>
            {formik.touched.termsAndConditions && !!formik.errors.termsAndConditions && <FormFeedback type="invalid">{formik.errors.termsAndConditions}</FormFeedback>}
          </div>
          <div className="mandatory-info mt-2">Fields marked with * are mandatory</div>
          {isErrorVisible && <Alert color="danger" className="fade show text-center mb-0 mt-4">
            <i className="mdi mdi-alert-circle-outline me-2"></i>Unable to send data</Alert>}
          {isEmailErrorVisible && <Alert color="danger" className="fade show text-center mb-0 mt-4">
            <i className="mdi mdi-alert-circle-outline me-2"></i>{textError}</Alert>}
          <Button onClick={handleRegisterForm} className="btn btn sign-up-btn" type="submit" disabled={formik.isSubmitting || !recaptchaValue}>
            Register Now!
          </Button>
        </Form>
      </div>
    </Container>
    <FormMessage isOpen={isSuccessOpen} closeModal={() => setIsSuccessOpen(false)} image={oneStepAwayMessages.image} title={oneStepAwayMessages.title} description={oneStepAwayMessages.description} />
  </section>)
}

const usStates = toSelectOptions(UsStates);

export default FormSection;